import React, { useContext, useEffect, useState, useMemo } from "react";
import { FilterContext } from "../../context/Filter";
import { axInstBasic } from "../Service/Service";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";

export const getArnPayload = (parArn, childArn, filters = {}) => {
  if (!parArn && !childArn) {
    throw new Error("No ARN provided for complaints count filter.");
  }
  return {
    ...filters,
    ...(parArn ? { parArn } : {}),
    ...(childArn ? { childArn } : {}),
  };
};

export const dropDownTableOptions = async (
  parArn,
  childArn,
  filters = {},
  search_type
) => {
  const values = getArnPayload(parArn, childArn, filters);
  const payload = { ...values, search_type };
  try {
    const res = await axInstBasic.post(
      `${process.env.REACT_APP_API_URL}dropDownTableOptions`,
      payload
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

export const dropDownTableData = async (
  parArn,
  childArn,
  filters = {},
  search_type,
  dropdownvalue
) => {
  const values = getArnPayload(parArn, childArn, filters);
  const payload = { ...values, search_type, dropdownvalue };
  try {
    const res = await axInstBasic.post(
      `${process.env.REACT_APP_API_URL}dropDownTableData`,
      payload
    );
    return res.data;
  } catch (error) {
    throw error;
  }
};

const Tabledetails = () => {
  const {
    arn: parArn,
    childArn,
    complaintsPayload,
    setComplaintsPayload,
    isFilterReady,
    open,
  } = useContext(FilterContext);

  const shouldEnableQuery = Boolean(parArn || childArn);
  const [loading, setLoading] = useState(true);
  const [selectedDropDownOption, setSelectedDropDownOption] = useState("");
  const [selectedDropdownOptionTable, setSelectedDropdownOptionTable] =
    useState("");
  const [dropDownOptionData, setDropDownOptionData] = useState([]);
  const [isValueDropdownOpen, setIsValueDropdownOpen] = useState(false);
  const [valueSearch, setValueSearch] = useState("");

  const [tableData, setTableData] = useState(null);

  const handleDropDownOptionChange = async (e) => {
    const selectedValue = e.target.value;
    setSelectedDropDownOption(selectedValue);
    setSelectedDropdownOptionTable(""); // Reset second dropdown

    if (selectedValue && shouldEnableQuery) {
      try {
        setLoading(true);
        const dropDownTableOptionsResp = await dropDownTableOptions(
          parArn,
          childArn,
          complaintsPayload,
          selectedValue
        );

        let optionsData = [];
        if (
          dropDownTableOptionsResp &&
          dropDownTableOptionsResp.success &&
          Array.isArray(dropDownTableOptionsResp.result)
        ) {
          optionsData = dropDownTableOptionsResp.result;
        } else if (Array.isArray(dropDownTableOptionsResp)) {
          optionsData = dropDownTableOptionsResp;
        }

        setDropDownOptionData(optionsData);
      } catch (error) {
        setDropDownOptionData([]);
      } finally {
        setLoading(false);
      }
    } else {
      setDropDownOptionData([]);
    }
  };

  const handleSecondDropdownChange = async (e) => {
    const selectedValue = e.target.value;
    setSelectedDropdownOptionTable(selectedValue);

    if (selectedValue && selectedDropDownOption && shouldEnableQuery) {
      try {
        setLoading(true);
        const dropDownTableDataResp = await dropDownTableData(
          parArn,
          childArn,
          complaintsPayload,
          selectedDropDownOption,
          selectedValue
        );
        setTableData(dropDownTableDataResp || {});
      } catch (error) {
        setTableData(null);
      } finally {
        setLoading(false);
      }
    }
  };

  const refetchAllVehicleData = async () => {
    if (!shouldEnableQuery) return;

    setLoading(true);
    try {
      if (selectedDropDownOption) {
        const dropDownTableOptionsResp = await dropDownTableOptions(
          parArn,
          childArn,
          complaintsPayload,
          selectedDropDownOption
        );

        let optionsData = [];
        if (
          dropDownTableOptionsResp &&
          dropDownTableOptionsResp.success &&
          Array.isArray(dropDownTableOptionsResp.result)
        ) {
          optionsData = dropDownTableOptionsResp.result;
        } else if (Array.isArray(dropDownTableOptionsResp)) {
          optionsData = dropDownTableOptionsResp;
        }
        setDropDownOptionData(optionsData);

        if (selectedDropdownOptionTable) {
          const dropDownTableDataResp = await dropDownTableData(
            parArn,
            childArn,
            complaintsPayload,
            selectedDropDownOption,
            selectedDropdownOptionTable
          );
          setTableData(dropDownTableDataResp || {});
        }
      }
    } catch (error) {
      setDropDownOptionData([]);
    } finally {
      setLoading(false);
    }
  };
  const filteredDropDownOptions = useMemo(() => {
    if (!valueSearch) return dropDownOptionData;

    return dropDownOptionData.filter((item) =>
      String(item.key)
        .toLowerCase()
        .includes(valueSearch.toLowerCase())
    );
  }, [dropDownOptionData, valueSearch]);

  useEffect(() => {
    if (!isFilterReady) return;
    refetchAllVehicleData();
  }, [parArn, childArn, complaintsPayload, isFilterReady]);

  const data = useMemo(() => tableData?.result || [], [tableData]);

  const columns = useMemo(
    () => [
      { accessorKey: "CREATED_BY", header: "Created By" },
      { accessorKey: "COMPLAINT_DATE_TIME", header: "Complaint Date" },
      { accessorKey: "MODE", header: "Mode" },
      { accessorKey: "VEHICLE_REGISTRATION_NUMBER", header: "Registration No" },
      { accessorKey: "COMPLAINT_NO", header: "Complaint No" },
      { accessorKey: "CHASSIS_NO", header: "Chassis No" },
      { accessorKey: "PPL", header: "PPL" },
      { accessorKey: "DEALER", header: "Dealer" },
      { accessorKey: "DIVISION", header: "Division" },
      { accessorKey: "REGION", header: "Region" },
      { accessorKey: "COMPLAINT_STATUS", header: "Status" },
      { accessorKey: "AREA", header: "Area" },
      { accessorKey: "ORIGIN_POINT", header: "Origin Point" },
      { accessorKey: "PROBLEM_AREA", header: "Problem Area" },
      { accessorKey: "PL", header: "PL" },
      { accessorKey: "LOB", header: "LOB" },
      { accessorKey: "PRIMARY_COMPLAINT_AREA", header: "Primary Complaint Area" }, // added column
      { accessorKey: "Complaint_Open_Days", header: "Complaint Open Days" }, // added column
    ],
    []
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!isFilterReady) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <div className="text-xl font-semibold">Loading complaint data...</div>
      </div>
    );
  }

  const isSecondDropdownDisabled =
    !selectedDropDownOption || !Array.isArray(dropDownOptionData) || loading;

  return (
    <div className="p-4 bg-white rounded-xl justify-center justify-items-center shadow-md space-y-4">
      {/* First Dropdown */}
      <div className=" block md:flex w-48 md:w-[700px] gap-3 justify-center justify-items-center">
        <select
          value={selectedDropDownOption}
          onChange={handleDropDownOptionChange}
          disabled={loading}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
        >
          <option value="">Select Search Type</option>
          <option value="chassis_number">Chassis Number</option>
          <option value="vehicle_registration_number">
            Vehicle Registration Number
          </option>
          <option value="division_name">Division Name</option>
          <option value="problem_area">Problem Area</option>
          <option value="complaint_number">Complaint Number</option>
          <option value="PRIMARY_COMPLAINT_AREA">PRIMARY COMPLAINT AREA</option>
        </select>

        {/* Second Dropdown */}
        <div className="relative w-full">
          {/* Search Input */}
          <input
            type="text"
            value={valueSearch}
            placeholder={
              !selectedDropDownOption
                ? "Select Value"
                : loading
                  ? "Loading..."
                  : "Search value..."
            }
            disabled={isSecondDropdownDisabled}
            onChange={(e) => {
              setValueSearch(e.target.value);
              setIsValueDropdownOpen(true);
            }}
            onFocus={() => setIsValueDropdownOpen(true)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring focus:ring-blue-300 disabled:bg-gray-100"
          />

          {/* Dropdown List */}
          {isValueDropdownOpen && !isSecondDropdownDisabled && (
            <ul className="absolute z-20 w-full bg-white border border-gray-300 rounded mt-1 max-h-48 overflow-y-auto shadow">
              {filteredDropDownOptions.length === 0 ? (
                <li className="px-3 py-2 text-sm text-gray-500">
                  No results found
                </li>
              ) : (
                filteredDropDownOptions.map((item, index) => (
                  <li
                    key={`${item.key}-${index}`}
                    className="px-3 py-2 cursor-pointer hover:bg-blue-100 text-sm flex justify-between"
                    onClick={() => {
                      setSelectedDropdownOptionTable(item.key);
                      setValueSearch(item.key);
                      setIsValueDropdownOpen(false);

                      // 🔥 reuse existing handler
                      handleSecondDropdownChange({
                        target: { value: item.key },
                      });
                    }}
                  >
                    <span>{item.key}</span>
                    <span className="text-gray-400 text-xs">
                      ({item.doc_count})
                    </span>
                  </li>
                ))
              )}
            </ul>
          )}
        </div>

      </div>
      {loading && <div className="text-sm text-gray-500 mt-2">Loading...</div>}

      {tableData?.result?.length !== 0 && (
        <div className="p-4">
          <div
            className={`max-h-[400px] ${open ? "w-[900px]" : "w-[1100px]"
              } overflow-y-auto overflow-x-auto border rounded-lg`}
          >
            <table className=" w-96 md:min-w-full border border-gray-300">
              <thead className="sticky top-0 z-10">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="border border-gray-300 px-3 py-2 text-left text-sm font-semibold text-white bg-[#2374BF]"
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        // <td
                        //   key={cell.id}
                        //   className="border border-gray-300 px-3  text-sm text-gray-600"
                        // >
                        //   {flexRender(
                        //     cell.column.columnDef.cell,
                        //     cell.getContext()
                        //   )}
                        // </td>

                        <td
                          key={cell.id}
                          className="border border-gray-300 px-3 py-2 text-sm  whitespace-nowrap overflow-hidden text-ellipsis"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>

                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={columns.length}
                      className="text-center py-2 text-gray-500"
                    >
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tabledetails;
